"use client";
import {
  Flex,
  Button,
  Stack,
  Image,
  Grid,
  Text,
  Show,
  Box,
  Icon,
  Input,
  HStack,
  Checkbox,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
} from "@chakra-ui/react";
import { useGetAdminAllModels, useDeleteAdminModel, useGetExpiredModels } from "queries";
import { useState, useEffect } from "react";
import { InputWithDebounce } from "components";
import Link from "next/link";
import { usePathname } from "next/navigation";
import DatePicker from "react-datepicker"; // Import DatePicker component
import "react-datepicker/dist/react-datepicker.css"; // Import DatePicker styles
import { FaTrash } from "react-icons/fa";

export const ModelsPage = () => {
  const pathname = usePathname();
  const [page, setPage] = useState<number>(1);
  const [email, setEmail] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null); // State for start date
  const [endDate, setEndDate] = useState<Date | null>(null); // State for end date
  const [showOnlyPending, setShowOnlyPending] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const toast = useToast();
  const [modelTypeFilter, setModelTypeFilter] = useState<string>("all");

  const deleteAdminModel = useDeleteAdminModel(); // Use admin delete model hook

  // Function to convert date to a UTC date string without time
  const convertToUTCDateString = (date: Date): string => {
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    return utcDate.toISOString().split("T")[0];
  };

  const baseURL =
    "https://deepmodeimage.blob.core.windows.net/deepmode-model-image/";

    // Always call both hooks
    const expiredHook = useGetExpiredModels({
      startDate: startDate ? convertToUTCDateString(startDate) : new Date().toISOString().split("T")[0],
      endDate: endDate ? convertToUTCDateString(endDate) : new Date().toISOString().split("T")[0],
    });

    const allModelsHook = useGetAdminAllModels({
      page: showOnlyPending ? 1 : page,
      pageSize: showOnlyPending ? 1000 : 15,
      email,
      start_date: startDate ? convertToUTCDateString(startDate) : undefined,
      end_date: endDate ? convertToUTCDateString(endDate) : undefined,
      model_type: modelTypeFilter === "all" ? undefined : modelTypeFilter,
    });

    // Conditionally select result based on filter
    const data = modelTypeFilter === "expired" ? expiredHook.data : allModelsHook.data;
    const isLoading = modelTypeFilter === "expired" ? expiredHook.isLoading : allModelsHook.isLoading;
    const isError = modelTypeFilter === "expired" ? expiredHook.isError : allModelsHook.isError;
    const refetch = modelTypeFilter === "expired" ? expiredHook.refetch : allModelsHook.refetch;

        

  useEffect(() => {
    console.log("API Query Params:", {
      page: showOnlyPending ? 1 : page,
      pageSize: showOnlyPending ? 1000 : 15,
      email,
      start_date: startDate ? convertToUTCDateString(startDate) : undefined,
      end_date: endDate ? convertToUTCDateString(endDate) : undefined,
      model_type: modelTypeFilter === "all" ? undefined : modelTypeFilter, // Log this
    });
  }, [page, showOnlyPending, email, startDate, endDate, modelTypeFilter]);
  
  
  // Add a console log to check the value of modelTypeFilter
  useEffect(() => {
    console.log("Model type filter:", modelTypeFilter);
  }, [modelTypeFilter]);
  
  const useExpiredModelQuery = modelTypeFilter === "expired";
  const expiredQuery = useGetExpiredModels({
  startDate: startDate ? convertToUTCDateString(startDate) : new Date().toISOString(),
  endDate: endDate
    ? convertToUTCDateString(endDate)
    : new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
});



  // Reset page to 1 whenever the email, date, or "show only pending" changes
  useEffect(() => {
    setPage(1); // Reset to the first page on filter change
    refetch(); // Refetch data whenever filters change
  }, [email, startDate, endDate, showOnlyPending, modelTypeFilter]); // Include modelTypeFilter as a dependency
  

  const filteredModels = modelTypeFilter === "expired"
  ? expiredQuery.data?.models
  : showOnlyPending
    ? data?.models.filter((model: any) => {
        if (model.isFlux) return model.statusFlux !== "completed";
        return model.status !== "completed";
      })
    : data?.models;


  const handleEmailChange = (email: string) => {
    setEmail(email);
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  const nextPage = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const prevPage = () => {
    setPage((prevPage) => Math.max(1, prevPage - 1)); // Avoid going to negative or zero page numbers
  };

  const handleDeleteClick = (model: any) => {
    setSelectedModel(model); // Set the model to be deleted
    onOpen(); // Open the confirmation modal
  };

  const handleDeleteConfirm = () => {
    if (!selectedModel) return;

    deleteAdminModel.mutate(
      { model_id: selectedModel.id, model_username: selectedModel.username },
      {
        onSuccess: () => {
          toast({
            title: "Model Deleted",
            description: `Model ${selectedModel.display_name} has been deleted successfully.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          onClose();
          refetch(); // Refresh the models list after deletion
        },
        onError: (error) => {
          console.error("Error deleting model:", error);
          toast({
            title: "Model Deleted",
            description: `Model ${selectedModel.display_name} has been deleted successfully.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          onClose();
          refetch();
        },
      }
    );
  };

  const isModelExpired = (model: any): boolean => {
    const now = new Date();
  
    if (model.isFlux && model.updated_at) {
      const updated = new Date(model.updated_at);
      const expirationDate = new Date(updated.getTime() + 30 * 24 * 60 * 60 * 1000);
      return expirationDate < now;
    }
  
    if (!model.isFlux && model.expire_at) {
      const expireAt = new Date(model.expire_at);
      return expireAt < now;
    }
  
    return false;
  };
  
  

  return (
    <Flex direction="column" align="center" p={5}>
      <InputWithDebounce
        placeholder="Filter by user email"
        delay={500}
        value={email}
        onChange={(e) => handleEmailChange(e)}
        mb={4}
        width="100%"
      />

      {/* Date range filter section */}
      <HStack align="center" justify="center" mb={4} spacing={4}>
        <Box>
          <Text mb={2}>Start Date:</Text>
          <Box>
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              dateFormat="yyyy-MM-dd" // Display format for the date
              placeholderText="Select start date"
              isClearable // Allow clearing the selected date
              customInput={<Input />}
            />
          </Box>
        </Box>
        <Box>
          <Text mb={2}>End Date:</Text>
          <Box>
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              dateFormat="yyyy-MM-dd" // Display format for the date
              placeholderText="Select end date"
              isClearable // Allow clearing the selected date
              customInput={<Input />}
            />
          </Box>
        </Box>
         {/* Model Type Filter */}
         <Box>
          <Text mb={2}>Model Type:</Text>
          <Select
            value={modelTypeFilter}
            onChange={(e) => setModelTypeFilter(e.target.value)} // Update the state
          >
            <option value="all">All</option>
            <option value="flux">FLUX</option>
            <option value="normal">Normal</option>
            <option value="expired">Expired</option>
          </Select>
        </Box>
      </HStack>

      

      {/* Pending models filter section */}
      <Checkbox
        isChecked={showOnlyPending}
        onChange={(e) => setShowOnlyPending(e.target.checked)}
        mb={4}
      >
        Show Only Pending Models
      </Checkbox>

      

      <Stack spacing={4} direction="row" align="center" justify="center" mb={4}>
        <Button onClick={prevPage} disabled={page === 1 || showOnlyPending || modelTypeFilter === "expired"}>
          Previous
        </Button>
        <Button onClick={nextPage} disabled={showOnlyPending || modelTypeFilter === "expired"}>
          Next
        </Button>
      </Stack>

      <Flex align="center" direction="column" width="100%" maxW="1200px">
      {(isLoading || (useExpiredModelQuery && expiredQuery.isLoading)) ? (
            <p>Loading...</p>
          ) : (isError || (useExpiredModelQuery && expiredQuery.isError)) ? (
            <p>Error loading models.</p>
          ) : (
          <Grid gap={3} padding={5} templateColumns={"repeat(3,1fr)"}>
            {filteredModels.map((model: any) => (
              <Flex
                key={model.id}
                position="relative"
                direction="column"
                w={{ base: "142px", md: "180px", lg: "264px" }}
              >
                {/* Delete Button */}
                <Button
                  size="xs"
                  colorScheme="red"
                  position="absolute"
                  top="2"
                  left="2"
                  onClick={() => handleDeleteClick(model)}
                >
                  <Icon as={FaTrash} boxSize={4} />
                </Button>

                <Link href={`${pathname}/${model.id}`}>
                  <Image
                    boxShadow="0 0 5px 5px rgb(44, 49, 77)"
                    borderRadius="14px"
                    h={{ base: "142px", md: "180px", lg: "264px" }}
                    w={{ base: "142px", md: "180px", lg: "264px" }}
                    src={model.thumbnail}
                    objectFit={"cover"}
                    alt="model_image"
                    border="none"
                  />
                </Link>

                {/* FLUX / Normal tag at bottom-right over image */}
                    <Box
                      position="absolute"
                      bottom="2"
                      right="2"
                      bg={model.isFlux ? "purple.500" : "green.700"}
                      color="white"
                      px="8px"
                      py="2px"
                      fontSize="xs"
                      fontWeight="bold"
                      borderRadius="md"
                      zIndex="1"
                      boxShadow="sm"
                    >
                      {model.isFlux ? "FLUX" : "Normal"}
                    </Box>

                    {/* EXPIRED TAG */}
                    {isModelExpired(model) && (
                      <Text
                        position="absolute"
                        top="15%" // closer to Pending
                        left="50%"
                        transform="translateX(-50%)"
                        fontSize="14px"
                        px="10px"
                        py="4px"
                        borderRadius="8px"
                        bg="rgba(139, 0, 0, 0.6)" // darker transparent red
                        color="white"
                        fontWeight="semibold"
                        zIndex="2"
                      >
                        Expired
                      </Text>
                    )}

                    {/* PENDING TAG */}
                    {((model.isFlux && model.statusFlux !== "completed") || (!model.isFlux && model.status !== "completed")) && (
                      <Text
                        position="absolute"
                        top="30%" // closer below Expired
                        left="50%"
                        transform="translateX(-50%)"
                        fontSize="14px"
                        px="10px"
                        py="4px"
                        borderRadius="8px"
                        bg="blackAlpha.700"
                        color="white"
                        fontWeight="semibold"
                        zIndex="2"
                      >
                        Pending
                      </Text>
                    )}
                <Flex
                  bg="black"
                  borderRadius="32px"
                  position="absolute"
                  top={{ base: "2", lg: "4" }}
                  right={{ base: "2", lg: "4" }}
                  zIndex="1"
                  p={{ base: "4px", lg: "8px" }}
                  minW={{ base: "25px", md: "" }}
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text fontWeight="semibold" fontSize="sm" color="white">
                    {model.total_image_generations ?? 0}
                  </Text>
                  {/* Desktop, >= 768px */}
                  <Show above="md">
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color="white"
                      ml="4px"
                    >
                      Creations
                    </Text>
                  </Show>
                </Flex>
                

                {model.email && <Text ml="8px">by: {model.email}</Text>}
                {model.email && (
                <Text ml="8px" color="gray.500" fontSize="sm">
                  Model Name: <Text as="span" fontWeight="bold" color="white">{model.username}</Text>
                </Text>
              )}
                {model.created_at && (
                  <Text ml="8px" color="gray.500" fontSize="sm">
                    Created At: {new Date(model.created_at).toLocaleDateString()}
                  </Text>
                )}
                {(model.expire_at || model.isFlux) && (
                  <Text ml="8px" color="red.500" fontSize="sm">
                    Expires At:{" "}
                    {model.isFlux
                      ? new Date(new Date(model.updated_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString() // Flux models: updated_at + 30 days
                      : new Date(model.expire_at).toLocaleDateString()} 
                  </Text>
                )}
              </Flex>
            ))}
          </Grid>
        )}
      </Flex>

      {/* Confirmation Modal for Deletion */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <Text color="black">
              Are you sure you want to delete this model?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={handleDeleteConfirm}>
              Delete
            </Button>
            <Button ml={3} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default ModelsPage;
